// src/components/pat/PATEvidencePanel.jsx
// Sentence-level evidence audit for PAT-GPT.
// Renders qualitative evidence items with claim-link context.

import React from "react";

function roleColour(role) {
  switch ((role || "").toLowerCase()) {
    case "support":
      return "text-emerald-300 bg-emerald-900/40 border-emerald-700/80";
    case "counter":
      return "text-rose-300 bg-rose-900/40 border-rose-700/80";
    default:
      return "text-slate-200 bg-slate-800/60 border-slate-600/80";
  }
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normaliseRole(role) {
  const r = (role || "").toLowerCase().trim();
  if (r === "support" || r === "counter") return r;
  return "neutral";
}

function buildEvidenceLinkMap(links) {
  const map = new Map();
  const rows = safeArray(links?.links);

  rows.forEach((row, idx) => {
    const subclaimLabel =
      typeof row?.subclaim_index === "number"
        ? `S${row.subclaim_index + 1}`
        : `S${idx + 1}`;

    const allIds = [
      ...safeArray(row?.supporting_evidence_ids),
      ...safeArray(row?.counter_evidence_ids),
      ...safeArray(row?.neutral_evidence_ids),
    ];

    allIds.forEach((id) => {
      if (typeof id !== "string" || !id.trim()) return;

      if (!map.has(id)) {
        map.set(id, new Set());
      }

      map.get(id).add(subclaimLabel);
    });
  });

  return map;
}

export default function PATEvidencePanel({ evidence, links, audit }) {
  const items = safeArray(evidence?.evidence_items);
  if (items.length === 0) return null;

  const auditItems = safeArray(audit?.evidence_items);
  const auditItemById = new Map();
  for (const item of auditItems) {
    if (typeof item?.id === "string" && item.id.trim()) {
      auditItemById.set(item.id, item);
    }
  }

  const directLinkMap = buildEvidenceLinkMap(links);
  const unlinkedIds = new Set(safeArray(links?.unlinked_evidence_ids));

  const linkedCount =
    typeof audit?.evidence_overview?.linked_items === "number"
      ? audit.evidence_overview.linked_items
      : null;

  const unlinkedCount =
    typeof audit?.evidence_overview?.unlinked_items === "number"
      ? audit.evidence_overview.unlinked_items
      : null;

  const footerNote = audit?.notes || links?.summary || "";

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 text-xs text-slate-100">
      <div className="mb-3 flex flex-col gap-1">
        <div className="text-sm font-semibold text-indigo-300">
          Sentence-level evidence audit
        </div>

        <div className="text-[0.7rem] text-slate-400">
          {items.length} sentence-level item
          {items.length !== 1 ? "s" : ""}
          {linkedCount !== null && unlinkedCount !== null
            ? ` · ${linkedCount} linked · ${unlinkedCount} unlinked`
            : ""}
        </div>
      </div>

      {evidence?.summary && (
        <p className="mb-2 leading-snug text-slate-300">{evidence.summary}</p>
      )}

      <div className="space-y-2">
        {items.map((item, idx) => {
          const role = normaliseRole(item?.role);
          const auditRow =
            typeof item?.id === "string"
              ? auditItemById.get(item.id) || null
              : null;

          const directLinkedSubclaims =
            typeof item?.id === "string"
              ? Array.from(directLinkMap.get(item.id) || [])
              : [];

          const fallbackLinkedSubclaims = safeArray(auditRow?.linked_subclaims);

          const linkedSubclaims =
            directLinkedSubclaims.length > 0
              ? directLinkedSubclaims
              : fallbackLinkedSubclaims;

          const isUnlinked =
            linkedSubclaims.length === 0 &&
            (unlinkedIds.has(item?.id) ||
              (typeof auditRow?.is_unlinked === "boolean"
                ? auditRow.is_unlinked
                : false));

          return (
            <div
              key={item?.id || `evidence-row-${idx}`}
              className={`rounded-lg border px-3 py-2 ${roleColour(role)}`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-[0.68rem] font-semibold uppercase tracking-wide">
                      {item?.id || "E?"}
                    </span>

                    <span className="text-[0.68rem] text-slate-300">
                      {role}
                    </span>

                    {item?.type && (
                      <span className="text-[0.68rem] text-slate-300">
                        Type: {item.type}
                      </span>
                    )}

                    {item?.strength_label && (
                      <span className="text-[0.68rem] text-slate-300">
                        Strength: {item.strength_label}
                      </span>
                    )}
                  </div>

                  <div className="text-[0.78rem] leading-snug text-slate-100">
                    {item?.text || "—"}
                  </div>
                </div>

                <div className="flex-shrink-0 sm:text-right">
                  {linkedSubclaims.length > 0 ? (
                    <div className="text-[0.68rem] text-slate-200">
                      Linked to {linkedSubclaims.join(", ")}
                    </div>
                  ) : isUnlinked ? (
                    <div className="text-[0.68rem] text-slate-400">
                      Unlinked to a specific subclaim
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {footerNote && (
        <p className="mt-3 text-[0.7rem] leading-snug text-slate-400">
          {footerNote}
        </p>
      )}
    </div>
  );
}
